import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaEye, FaEyeSlash } from 'react-icons/fa';
import { URL, ADMIN } from "../../../../lib/lib.js";
import "./component.css";

const Component = ({componentTitle}) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    numberOfTables: '',
    address: {
      street: '',
      area: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    billingDetails: {
      panNumber: '',
      gstNumber: '',
      bankAccountNumber: '',
      ifscCode: '',
      upiId: '',
      billingEmail: ''
    }
  });
  const [isPhoneValid, setIsPhoneValid] = useState(null);
  const [isEmailValid, setIsEmailValid] = useState(null);
  const [passwordShown, setPasswordShown] = useState(false); 

  // Fetch user data if editing (for pre-filling the form)
  useEffect(() => {
    const getUserDetails = async () => {
      try {
        const response = await fetch(URL + ADMIN.hotel_view, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({id}),
          credentials: 'include'
        });
        const result = await response.json();
        const data = result.data;
        if (data) {
          setFormData({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            password: '',
            numberOfTables: data.numberOfTables || '',
            address: {
              street: data.address?.street || '',
              area: data.address?.area || '',
              city: data.address?.city || '',
              state: data.address?.state || '',
              postalCode: data.address?.postalCode || '',
              country: data.address?.country || ''
            },
            billingDetails: {
              panNumber: data.billingDetails?.panNumber || '',
              gstNumber: data.billingDetails?.gstNumber || '',
              bankAccountNumber: data.billingDetails?.bankAccountNumber || '',
              ifscCode: data.billingDetails?.ifscCode || '',
              upiId: data.billingDetails?.upiId || '',
              billingEmail: data.billingDetails?.billingEmail || ''
            }
          });
        }
      } catch (err) {
        console.error("Failed to fetch user details:", err);
      }
    };
  
    if (id) {
      getUserDetails();
    }
  }, [id]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes("address.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({ ...prev, address: {...prev.address, [key]: value }}));
      return;
    }

    if (name.includes("billingDetails.")) {
      const key = name.split(".")[1];
      setFormData((prev) => ({ ...prev, billingDetails: { ...prev.billingDetails, [key]: value } }));
      return;
    }


    // Handling form input changes
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'email' ? value.toLowerCase() : value, // Ensure email is always lowercase
    }));

    // Phone validation
    if (name === 'phone') {
      if (value.length === 10 && /^[0-9]+$/.test(value)) {
        setIsPhoneValid(true);
      } else {
        setIsPhoneValid(false);
      }
    }

    // Email validation
    if (name === 'email') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (emailRegex.test(value.toLowerCase())) {
        setIsEmailValid(true);
      } else {
        setIsEmailValid(false);
      }
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setPasswordShown(!passwordShown);
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const dataToSend = { ...formData };
    if (id){
      dataToSend.id = id;
      if (!dataToSend.password?.trim()) delete dataToSend.password;
    } 
    
    try {
      let request;
      if (id) {
        request = await fetch(URL + ADMIN.hotel_update, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dataToSend),
          credentials: 'include'
        });
      }else{
        request = await fetch(URL + ADMIN.hotel_create, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(dataToSend),
          credentials: 'include'
        });
      }

      if (request.status === 201 || request.status === 200) {
        let response = await request.json();
        console.log(response);
        alert(response.message);
        navigate('/admin/hotel');
      }
      
    } catch (error) {
      console.error("Error:", error);
      alert(error.response?.data?.message || "Something went wrong.");
    }
  };

  return (
    <div id="adduser" className="container-fluid">
      <div className="row profile-my_flx_profile_up">
        <div className="col-xl-2 col-lg-3 col-md-2 profile-pro1 profile-admin">
          <Link to="/admin/hotel">
            <button className="profile-back">
              <i className="ri-arrow-left-s-line profile-lefticon"></i>
            </button>
          </Link>
        </div>
        <div className="col-xl-8 col-lg-6 col-md-8">
          <div className="profile-my_form_signup_profile">

            <form onSubmit={handleSubmit}>
              <p className="profile-login_head">{id ? "Edit" : "Create"} {componentTitle}</p>

              <h5><b>Basic Details</b></h5>
              <div className="mb-3 m-2 row">
                <div className="col-md-6 p-2">
                  <label className="form-label profile-user_label">Hotel Name</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} className="form-control" placeholder="Enter Name" required />
                </div>

                <div className="col-md-6 p-2">
                  <label className="form-label profile-user_label">Phone No.</label>
                  <div className="profile-inputContainer">
                    <input type="text" name="phone" value={formData.phone} onChange={handleChange} maxLength={10} className="form-control profile-inputField" placeholder="Enter phone number" required />
                    {isPhoneValid !== null && (isPhoneValid ? (<FaCheckCircle className="profile-icon" color="green" />) : (<FaTimesCircle className="profile-icon" color="red" />))}
                  </div>
                </div>

                <div className="col-md-6 p-2">
                  <label className="form-label profile-user_label">Email</label>
                  <div className="profile-inputContainer">
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="form-control profile-inputField" placeholder="Enter Email" required />
                    {isEmailValid !== null && (isEmailValid ? (<FaCheckCircle className="profile-icon" color="green" />) : (<FaTimesCircle className="profile-icon" color="red" />))}
                  </div>
                </div>
              
                <div className="col-md-6 p-2">
                  <label className="form-label profile-user_label">Password</label>
                  <div className="profile-inputContainer">
                    <input type={passwordShown ? "text" : "password"} name="password" value={formData.password} onChange={handleChange} className="form-control" placeholder="Enter Password" required={!id} />
                    <span onClick={togglePasswordVisibility} className="eye-icon">{passwordShown ? <FaEyeSlash /> : <FaEye />}</span>
                  </div>
                </div>

                <div className="col-md-6 p-2">
                  <label className="form-label profile-user_label">Number of Tables</label>
                  <input type="number" name="numberOfTables" value={formData.numberOfTables} onChange={handleChange} className="form-control" placeholder="Enter number of tables" />
                </div>

              </div>

              <h5><b>Address Details</b></h5>
              <div className=" m-2 mb-3 row">
                {["street", "area", "city", "state", "postalCode", "country"].map(field => (
                  <div className="col-md-6 p-2" key={field}>
                    <label className="form-label profile-user_label">{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                    <input
                      type="text"
                      name={`address.${field}`}
                      value={formData.address[field]}
                      onChange={handleChange}
                      className="form-control"
                      placeholder={`Enter ${field}`}
                    />
                  </div>
                ))}
              </div>

              <h5><b>Billing Details</b></h5>
              <div className=" m-2 mb-3 row">
                {["panNumber", "gstNumber", "bankAccountNumber", "ifscCode", "upiId", "billingEmail"].map(field => (
                  <div className="col-md-6 p-2" key={field}>
                    {/* <label className="form-label profile-user_label">{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label> */}
                    {/* <label className="form-label profile-user_label">{field.replace(/([A-Z])/g, ' $1')}</label> */}
                    <label className="form-label profile-user_label">{field.charAt(0).toUpperCase() + field.slice(1)}</label>
                    <input
                      type="text"
                      name={`billingDetails.${field}`}
                      value={formData.billingDetails[field]}
                      onChange={handleChange}
                      className="form-control"
                      placeholder={`Enter ${field}`}
                    />
                  </div>
                ))}
              </div>


              <button type="submit" className="profile-submit_bot1">{id ? "Update" : "Create"} {componentTitle}</button>
            </form>
          </div>
        </div>
        <div className="col-xl-2 col-lg-3 col-md-2"></div>
      </div>
    </div>
  );
};

export default Component;
